import { store } from '~/Store'
import { getNodeLabel, getRelationkeyLabel, splitUid, getUid, getNamespaceLabel } from 'Services/WeaverUtils'
import { getGroupForType, getGroup, getTypes } from './groups'
import { NODE, ATTRIBUTE_NODE, ATTRIBUTE_VALUE, RELATION, RELATION_NODE } from 'Types'
import { notification } from 'antd'

const getShowAttributes = () => store.getState().graph.showAttributes
const getShowAttributeAndRelationNodes = () => store.getState().graph.showAllMetaNodes
const getTypesMap = () => store.getState().explore.typesMap
const getHideRelationLabels = () => JSON.parse(localStorage.getItem('hideRelationLabels'))

export const renderGraphInfo = ({ description, message }) => {
  return notification.info({ description, message })
}

export const getRelevantEntityIdFromString = (type, id) => {
  if (type === 'relation-node') {
    return `relation-node-${id}`
  } else if (type === 'attribute-node') {
    return `attribute-node-${id}`
  } else if (type === 'node') {
    return `node-${id}`
  } else {
    return type + '-' + id
  }
}

export const getRelevantEntityIdFromNode = (node) => {
  if (node instanceof Weaver.RelationNode) {
    return `relation-node-${getUid(node)}`
  } else if (node instanceof Weaver.AttributeNode) {
    return `attribute-node-${getUid(node)}`
  } else if (node instanceof Weaver.Node) {
    return `node-${getUid(node)}`
  }
}

const updateObject = (oldObject, newValues) => ({
  ...oldObject,
  ...newValues
})

const getNodeProperties = (node, id) => {
  const parsedUid = getUid(node)
  const isType = !!getTypesMap()[parsedUid]
  const colorByGraph = JSON.parse(localStorage.getItem('colorByGraph'))
  return {
    id,
    isType,
    wvrId: parsedUid,
    label: getNodeLabel(node),
    entityType: NODE,
    shapeProperties: {
      borderDashes: !node._loaded ? [4, 4] : false
    },
    type: getTypes(node),
    graph: node.getGraph(),
    labels: node.getLabels() || [],
    group: isType && !colorByGraph ? `${getGroupForType(getTypes(node))}` : `${getGroup(node)}`
  }
}

const getRelNodeProperties = (node, key, hideRelationLabels, id) => {
  const parsedUid = getUid(node)
  const isType = !!getTypesMap()[parsedUid]
  const colorByGraph = JSON.parse(localStorage.getItem('colorByGraph'))
  return {
    id,
    wvrId: parsedUid,
    isType,
    type: getTypes(node),
    shape: 'hexagon',
    shapeProperties: {
      borderDashes: !node._loaded ? [4, 4] : false
    },
    graph: node.getGraph(),
    label: hideRelationLabels ? ' ' : getRelationkeyLabel(key, id),
    entityType: RELATION_NODE,
    from: getRelevantEntityIdFromNode(node.fromNode),
    to: getRelevantEntityIdFromNode(node.toNode),
    key,
    _label: getRelationkeyLabel(key, id),
    group: isType && !colorByGraph ? `${getGroupForType(getTypes(node))}` : `${getGroup(node)}`
  }
}

const getAttNodeProperties = (node, id) => {
  const parsedUid = getUid(node)
  const isType = !!getTypesMap()[parsedUid]
  const colorByGraph = JSON.parse(localStorage.getItem('colorByGraph'))
  return {
    id,
    wvrId: parsedUid,
    label: getNamespaceLabel(node.key),
    isType,
    type: getTypes(node),
    shape: 'triangle',
    shapeProperties: {
      borderDashes: !node._loaded ? [4, 4] : false
    },
    graph: node.getGraph(),
    labels: node.getLabels() || [],
    entityType: ATTRIBUTE_NODE,
    from: getRelevantEntityIdFromNode(node.fromNode),
    group: isType && !colorByGraph ? `${getGroupForType(getTypes(node))}` : `${getGroup(node)}`
  }
}

const getAttValueProperties = (node, id) => {
  const parsedUid = getUid(node)
  const isType = !!getTypesMap()[parsedUid]
  return {
    id,
    wvrId: parsedUid,
    label: node?.value?.toString(),
    isType,
    type: getTypes(node),
    shape: 'square',
    graph: node.getGraph(),
    labels: node.getLabels() || [],
    entityType: ATTRIBUTE_VALUE,
    from: getRelevantEntityIdFromNode(node.fromNode),
    group: `${getGroup(node)}`
  }
}

const getEdgeProperties = (key, fromNodeId, toNodeId, node, hideRelationLabels, id, edgeProps) => {
  const showAllMetaNodes = JSON.parse(localStorage.getItem('showAllMetaNodes'))
  return {
    ...edgeProps,
    id,
    _id: id,
    wvrId: getUid(node),
    from: fromNodeId,
    _from: fromNodeId,
    to: toNodeId,
    _to: toNodeId,
    selfReference: {
      size: 40
    },
    label: hideRelationLabels || showAllMetaNodes ? ' ' : getRelationkeyLabel(key, id),
    _label: hideRelationLabels || showAllMetaNodes ? ' ' : getRelationkeyLabel(key, id),
    graph: node.graph,
    hasRelations: _.size(node.relations()) > 0,
    key,
    entityType: node instanceof Weaver.AttributeNode ? ATTRIBUTE_NODE : RELATION,
    physics: fromNodeId !== toNodeId,
    scaling: {
      label: false,
      max: 6
    }
  }
}

const createRelationInGraph = async (relKeys, fromIds, toIds) => {
  const toArray = (item) => Array.isArray(item) ? item : [item]
  const fetchNode = async (id) => {
    const [uid, graph] = splitUid(id)
    return new Weaver.Query()
      .restrict([uid])
      .inGraph(graph)
      .withRelations()
      .first()
  }

  fromIds = toArray(fromIds)
  toIds = toArray(toIds)
  relKeys = toArray(relKeys)

  const fromNodes = await Promise.all(fromIds.map(fetchNode))

  const toIdsReduced = toIds.reduce((acc, toId) => {
    const [toUid, toGraph] = splitUid(toId)
    return {
      ...acc,
      [toGraph]: [...(acc[toGraph] || []), toUid]
    }
  }, {})

  const fetchNodes = async ([toGraph, ids]) => {
    const graph = toGraph === 'undefined' ? undefined : toGraph
    return new Weaver.Query()
      .restrict(ids)
      .withRelations()
      .inGraph(graph)
      .find()
  }

  const toNodes = _.flatten(await Promise.all(Object.entries(toIdsReduced).map(fetchNodes)))

  const createRelNode = (relKey, toNode, fromNode) => {
    const relNode = fromNode.relation(relKey).add(toNode)
    relNode.key = relKey
    return relNode
  }

  const relNodes = _.flattenDeep(
    relKeys.map((relKey) =>
      toNodes.map((toNode) =>
        fromNodes.map((fromNode) => createRelNode(relKey, toNode, fromNode))
      )
    )
  )

  await Weaver.Node.batchSave(fromNodes, { commitMessage: "Create relation(s) in graph view." })
  return [...fromNodes, ...relNodes]
}

const propertyCount = (n) => {
  if (n === undefined) {
    return 0
  }
  const attRecords = _.flatMap(n._attributes, (att) => att._records)
  const relRecordsOut = _.flatMap(n._relations, (rel) => rel._records)
  const relRecordsIn = _.flatMap(n._relationsIn, (rel) => rel._records)
  const records = [...attRecords, ...relRecordsOut, ...relRecordsIn]
  return records.length
}

const removeVisPrefix = (prefixedId) => {
  const entityPrefixes = [
    'node-',
    'attribute-edge-from-',
    'attribute-edge-to-',
    'attribute-edge-',
    'attribute-value-',
    'attribute-node-',
    'relation-edge-from-',
    'relation-edge-to-',
    'relation-edge-',
    'relation-node-'
  ]

  const relevantPrefix = entityPrefixes.find(prefix => _.startsWith(prefixedId, prefix))
  return _.replace(prefixedId, relevantPrefix, '')
}

export {
  createRelationInGraph,
  getAttNodeProperties,
  getAttValueProperties,
  getEdgeProperties,
  getNodeProperties,
  getRelNodeProperties,
  getShowAttributes,
  getShowAttributeAndRelationNodes,
  getTypesMap,
  getHideRelationLabels,
  propertyCount,
  updateObject,
  removeVisPrefix
}
